// appLyricsAdv — the "Advanced options" screen of the lyrics editor: cut and glue,
// the Edit mode, syllables, bulk time shift, break templates. Keys mirror
// ../pl/appLyricsAdv.ts exactly.
//
// THE RULE ON THIS SCREEN: hand-correcting lyrics is hours of an artist's work.
// Every operation that can overwrite it says EXACTLY what disappears and how much
// of it — never just "are you sure?". Hence the counters in "Clear manual marks?"
// and "Load …?".
//
// RULE 8 (MINIMAL, round R4b of session 32): the sentences that explained the
// mechanic are gone. "Click the gap between words", "Clicking the same gap cycles…",
// "A glue and a break cannot share the same gap", "Ctrl+Z undoes every step" — all
// four taught what is now visible: a control is greyed exactly where the operation
// is impossible, and the shortcuts stand in the legend under the list.
//
// NUMBERS IN SENTENCES: `t()` has no plural selection, so a count on this screen
// always stands appositively ("Words: 54") or in the pair "{n} / {max}". English
// does not need the trick, but the KEY SET must match, so the shapes stay aligned.
export const appLyricsAdv = {
  // ── "Cut and glue": a row of the list is ONE subtitle clip ──────────────────
  // The target is a WORD and the operation applies to the boundary BEFORE it. Two
  // explicit actions instead of a hidden three-state cycle inside one click.
  cutGlue: {
    title: 'Cut and glue',
    cutHere: 'Cut here',
    joinRows: 'Join lines',
    glue: 'Glue',
    unglue: 'Unglue',
    joinPrev: 'Join with previous',
    tooLongShort: 'too long',
    // One sentence in the first-run tip — it fits a finger and a mouse alike.
    styleLabel: 'Target style',
    targetLabel: 'Target style',
  },
  // The shortcut legend under the list (1440). It says what a key does, not what
  // the key is called.
  legend: {
    boundary: 'boundary',
    cut: 'cut',
    joinGlue: 'join / glue',
    unglue: 'unglue',
    play: 'play',
    undo: 'undo',
    redo: 'redo',
  },
  // A boundary is an element with a name — a button in reading order between words.
  boundary: {
    label: 'boundary before {word}: {state}',
    stateAuto: 'automatic',
    stateCut: 'end of clip',
    stateGlued: 'glued',
  },
  // Messages for the `aria-live` region: an operation with no view of its own.
  live: {
    cut: 'Line split before {word}',
    glue: 'Glued {a} and {b}',
    joined: 'Lines joined',
    unglued: 'Unglued {a} and {b}',
  },
  meter: {
    label: 'Set capacity: {n} of {max}',
  },
  empty: 'No words to edit',
  emptyAction: 'Edit the lyrics',
  save: 'Save',
  saving: 'Saving…',
  saved: 'Saved',
  unsaved: 'Unsaved changes',
  error: {
    // The error code stands beside it and "Save" is still live, so the way out is
    // visible — "Try again." had nothing to add.
    save: 'Could not save.',
    retry: 'Try again',
  },
  advanced: {
    toggle: 'Advanced options',
    // The window shown when the lyrics have unsaved changes. This screen works on
    // the LAST SAVED version of the track, so one sentence says so and the second
    // action saves straight away instead of sending anyone back for nothing.
    saveFirstTitle: 'Open the advanced options?',
    saveFirst: 'Advanced options work on the last saved version.',
    saveFirstConfirm: 'Save and open',
    saveFirstCancel: 'Cancel',
  },
  mode: {
    label: 'Mode',
    edit: 'Edit',
    accents: 'Cut and glue',
  },
  tool: {
    glue: 'Glue',
    split: 'Split',
    syllables: 'Into syllables',
    merge: 'Merge words',
    mergeShort: 'Merge',
    delete: 'Delete word',
    insert: 'Insert word',
    insertShort: 'Insert',
    undo: 'Undo',
    redo: 'Redo',
  },
  more: {
    label: 'More actions',
  },
  timeline: {
    minimap: 'Whole track',
    // The waveform is drawn separately from the transcription — say the gap is temporary.
    wavePending: 'Drawing the waveform…',
    // The timeline does not disappear when the waveform fails to load, so the
    // sentence has to say where the shape somebody is still looking at comes from.
    waveUnavailable: 'The waveform is unavailable. The shape on the timeline comes from where the words fall, not from the recording.',
    waveRetry: 'Try again',
    gap: 'gap {s}',
    play: 'Play',
    pause: 'Pause',
    nudgeEarlier: '-{step} s',
    nudgeLater: '+{step} s',
    selectedWord: 'Selected word',
  },
  zoom: {
    label: 'Zoom',
    in: 'Zoom in',
    out: 'Zoom out',
    reset: 'Fit to window',
    lock: 'Lock zoom',
  },
  playhead: {
    follow: 'Follow playback',
    noAudio: 'No audio file - the timeline works, playback does not.',
  },
  // The "Edit" mode: the same rows, except the target is the word itself rather
  // than the boundary before it.
  words: {
    title: 'Words',
    label: 'word {word}',
    labelLow: 'word {word}, to check',
    lowConfidence: 'To check: {n}',
    filterLow: 'To check',
    noLow: 'Every word was recognised confidently.',
    noLowAction: 'Show all',
    fix: 'Fix',
    editLabel: 'Edit word {n}',
    keyCommit: 'save the word',
    keyCancel: 'discard the change',
    keyDelete: 'delete the word',
    keyUndo: 'undo',
    undoTitle: 'Undo the last change',
  },
  // Break set: the default one, or a separate one for a style category.
  target: {
    label: 'Break set',
    default: 'Default',
    category: '{name}',
  },
  autofill: {
    cta: 'Place automatically',
    done: 'Breaks placed: {count}',
  },
  clear: {
    ctaManual: 'Clear manual',
    title: 'Clear manual marks?',
    keep: 'Words and timings stay.',
    rowSet: 'Set',
    rowCuts: 'Own cuts',
    rowGlues: 'Glues',
  },
  shots: {
    preview: 'Preview',
    previewClose: 'Close the preview',
    // `{duration}` = the REAL clip length from the word timings ("3.2 s").
    previewMeta: 'clip {index} · {duration}',
    noFrame: 'Frame will come',
    seconds: '{s} s',
  },
  dialog: {
    cancel: 'Cancel',
  },
  retranscribe: {
    cta: 'Transcribe again',
    title: 'You will lose your corrections',
    lossHead: 'What disappears',
    lossWords: 'corrected words',
    lossBreaks: 'line breaks',
    lossGlues: 'syllable glues',
    lossTemplates: 'saved lyric templates',
    // Confirmation by typing a word — the one barrier you cannot click through by
    // reflex. `confirmWord` MUST stay ONE word, no spaces and nothing awkward to
    // type: the user copies it off the screen to unlock the button, so a two-word
    // translation switches the gate off in the other language.
    typeToConfirm: 'Type {word} to confirm',
    confirmWord: 'DELETE',
    failSafe: 'A failed transcription deletes nothing.',
    confirm: 'Transcribe',
    working: 'Transcribing…',
    queued: 'Queued - transcription starts in a moment.',
    failed: 'The transcription failed. Your corrections are untouched.',
  },
  syllables: {
    title: 'Split “{word}” into syllables',
    before: 'BEFORE',
    after: 'AFTER',
    confirm: 'Split',
    // When blocked, the window shows AFTER equal to BEFORE and a greyed action;
    // the REASON (one syllable, or too short) travels in the screen-reader name.
    blocked: 'This word cannot be split',
  },
  shift: {
    cta: 'Shift timings',
    title: 'Shift timings',
    hint: 'For when the whole transcription runs a fraction of a second late.',
    fromHereRight: 'From this word rightwards',
    toHereLeft: 'Leftwards',
    amount: 'Shift by',
    preview: 'Preview',
    wordCount: 'Words: {n}',
    // The reason the disabled “Shift 0” cannot run — reader only, and in `title`.
    blocked: 'The shift is zero seconds, so no word changes its timing.',
    clamped: 'Some words hit the start or the end of the track - those stayed where they were.',
    confirm: 'Shift {n}',
  },
  trim: {
    cta: 'Trim the track',
    title: 'Trim the track',
    from: 'From',
    to: 'To',
    fadeIn: 'Fade in',
    fadeOut: 'Fade out',
    newTrackNote: 'This creates a new track - the original stays untouched.',
    confirm: 'Trim {span}',
    working: 'Trimming…',
    done: 'Done. Words carried over: {n}',
    failed: 'Could not trim. The original is untouched.',
    tooShort: 'That section is too short - pick at least a second.',
  },
  template: {
    menu: 'Break templates',
    name: 'Template name',
    namePlaceholder: 'e.g. Fast styles',
    save: 'Save template',
    saving: 'Saving…',
    words: 'Words: {n}',
    delete: 'Delete template',
    deleted: 'Deleted “{name}”',
    loaded: 'Loaded “{name}”',
    loadTitle: 'Load “{name}”?',
    loadConfirm: 'Load',
    overwriteNote: 'Loading a template replaces the current breaks in this set.',
    nameTaken: 'You already have a template with that name.',
    saveError: 'Could not save the template. Your breaks are untouched.',
    loadError: 'Could not load the template. Nothing was changed.',
    deleteError: 'Could not delete the template.',
  },
}
